import clsx from 'clsx';
import styles from './FaceAssignmentBar.module.css';

/**
 * Horizontal bar showing assigned players with interleaved add buttons.
 *
 * Single responsibility: Display and manage player assignment chips.
 * Does not contain business logic - receives all state via props.
 *
 * Layout: [^] | [+] Name [+] Name [+]
 * Each + button knows its position index for ordered insertion.
 */
export default function FaceAssignmentBar({
  orderedNames,
  collapsed,
  hoveredPersonId,
  onToggleCollapse,
  onAddPlayer,
  onEditPlayer,
  onRemovePlayer,
  onRestorePlayer,
  onHoverPerson,
}) {
  return (
    <div className={styles.bar}>
      <button
        className={styles.toggleBtn}
        onClick={onToggleCollapse}
        title={collapsed ? 'Show faces' : 'Hide faces'}
      >
        {collapsed ? 'v' : '^'}
      </button>

      <div className={styles.divider} />

      <div className={styles.names}>
        {orderedNames.length === 0 ? (
          <>
            <button
              className={styles.addBtnInline}
              onClick={() => onAddPlayer(0)}
              title="Add player by name"
            >
              +
            </button>
            <span className={styles.empty}>No players tagged</span>
            <button
              className={styles.addBtnInline}
              onClick={() => onAddPlayer(0)}
              title="Add player by name"
            >
              +
            </button>
          </>
        ) : (
          <>
            <button
              className={styles.addBtnInline}
              onClick={() => onAddPlayer(0)}
              title="Insert player before first"
            >
              +
            </button>
            {orderedNames.map((n, idx) => (
              <span key={n.person_id || idx} className={styles.chipGroup}>
                <PlayerChip
                  name={n}
                  isHovered={hoveredPersonId === n.person_id}
                  onEdit={() => onEditPlayer({ face: n.face, personId: n.person_id })}
                  onRemove={() =>
                    n.face ? onRemovePlayer(n.face) : onRemovePlayer(n.person_id, true)
                  }
                  onRestore={() => onRestorePlayer(n.person_id)}
                  onHover={onHoverPerson}
                />
                <button
                  className={styles.addBtnInline}
                  onClick={() => onAddPlayer(idx + 1)}
                  title={`Insert player after ${n.label || 'this player'}`}
                >
                  +
                </button>
              </span>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Individual player chip component.
 */
function PlayerChip({ name, isHovered, onEdit, onRemove, onRestore, onHover }) {
  const isManual = !name.face;
  const isSoftDeleted = name.softDeleted;

  const handleMouseEnter = () => onHover?.(name.person_id);
  const handleMouseLeave = () => onHover?.(null);

  if (isSoftDeleted) {
    return (
      <div
        className={clsx(styles.chip, styles.manual, styles.softDeleted)}
        title="Removed - click to restore"
        onClick={onRestore}
      >
        <span className={styles.chipNameDeleted}>
          {name.label || 'person'}
        </span>
        <span className={styles.chipRestore} title="Click to restore">
          +
        </span>
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.chip, isManual && styles.manual, isHovered && styles.chipHovered)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={styles.chipName}
        onClick={onEdit}
        title={
          isManual
            ? 'Manual entry (click to link to face)'
            : 'Edit player assignment'
        }
      >
        {isManual && <span className={styles.manualIcon}>+</span>}
        {name.label || 'person'}
      </button>
      <button
        className={styles.chipRemove}
        onClick={onRemove}
        title={
          name.face
            ? 'Remove player link from this face and IPTC'
            : 'Remove player from photo'
        }
      >
        x
      </button>
    </div>
  );
}
